import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  Compass, 
  FileText, 
  MessageSquare, 
  Search,
} from 'lucide-react';
import { clearAttempt, loadAttempt, newAttempt, saveAttempt } from '../components/readiness/attempt';
import { ReadinessInsights, ReadinessNextStep } from '../components/readiness/ReadinessInsights';

// Result content for each priority area - PRESERVED
const priorityAreaContent = {
  career_direction: {
    heading: "Your Priority Area: Career Direction",
    copy: "Your responses suggest that you may be putting effort into career preparation without having enough clarity about the role you're actually preparing for. Before adding more certifications or applications, getting clearer about your target role and what that role actually requires may help you focus your effort more effectively.",
    whyMatters: "Without a clearly defined target role, it becomes difficult to decide which skills to prioritise, how to position your resume, what interviews to prepare for and which opportunities to pursue.",
    actions: [
      "Select one primary target role.",
      "Review at least 10 relevant job descriptions.",
      "Identify the skills and requirements that appear repeatedly.",
    ],
    tieExplanation: "Several areas may need attention based on your responses. We recommend starting with Career Direction because clarity about your target role influences how you position your profile, prepare for interviews and approach your job search.",
    clarityIntro: "Your Career Readiness Check suggests that Career Direction should be your starting point.",
  },
  profile_strength: {
    heading: "Your Priority Area: Profile Positioning",
    copy: "Your experience or skills may be stronger than your profile currently communicates. If recruiters cannot quickly understand your fit for a role, relevant capability can easily get overlooked.",
    whyMatters: "Even relevant skills and experience can be overlooked if your resume and work samples do not quickly communicate why you fit the roles you're targeting.",
    actions: [
      "Make your target role clear on your resume.",
      "Present projects around your contribution, skills and outcomes.",
      "Align your LinkedIn profile with the direction you're targeting.",
    ],
    tieExplanation: "Several areas may need attention based on your responses. We recommend starting with Profile Positioning because how you present yourself determines whether recruiters and hiring managers can quickly understand your fit for the roles you're targeting.",
    clarityIntro: "Your Career Readiness Check suggests that Profile Positioning may deserve your attention first.",
  },
  interview_readiness: {
    heading: "Your Priority Area: Interview Readiness",
    copy: "Your responses suggest that knowing the subject may not be the biggest issue. Communicating your experience, projects and suitability confidently may need more attention.",
    whyMatters: "Knowing the subject is only part of an interview. Being able to communicate your experience, projects and thinking clearly can significantly influence how an interviewer understands your suitability.",
    actions: [
      "Prepare a concise 60-second professional introduction.",
      "Practise explaining two projects using Problem → Action → Result.",
      "Record yourself answering five common interview questions and review your responses.",
    ],
    tieExplanation: "Several areas may need attention based on your responses. We recommend starting with Interview Readiness because even strong profiles need to be communicated effectively when you're in front of an interviewer.",
    clarityIntro: "Your Career Readiness Check suggests that Interview Readiness may deserve your attention first.",
  },
  job_search: {
    heading: "Your Priority Area: Job Search Strategy",
    copy: "Your preparation may be stronger than your current approach to finding opportunities. Exploring or applying broadly without clear criteria can make it harder to focus your effort on roles that suit your profile.",
    whyMatters: "Strong preparation alone may not create opportunities if your search is too broad or relies on only one channel. A more targeted approach can help you focus your effort on relevant opportunities.",
    actions: [
      "Define clear criteria for the roles you will apply to.",
      "Track applications and outcomes.",
      "Combine applications with recruiter outreach, alumni conversations, networking and referrals.",
    ],
    tieExplanation: "Several areas may need attention based on your responses. We recommend starting with Job Search Strategy because even with good preparation, a scattered approach to finding opportunities can reduce your results.",
    clarityIntro: "Your Career Readiness Check suggests that your Job Search Strategy may deserve your attention first.",
  },
};

// Stage options - PRESERVED
export const CareerReadinessCheck = () => {
  const [step, setStep] = useState('landing'); // landing, assessment, lead-capture, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [bank, setBank] = useState(null); // V3 questions from the server (no scores)
  const [bankError, setBankError] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [leadData, setLeadData] = useState({
    firstName: '',
    email: '',
    whatsapp: '',
    stage: '',
    qualification: '',
    otherQualification: '',
    targetRole: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [handoff, setHandoff] = useState(null); // { token, firstName } when arriving from Talent Network
  const [consent, setConsent] = useState({ email: false, whatsapp: false });
  const [outcome, setOutcome] = useState({ isMember: false, emailedTo: '', error: '', prefill: null });
  
  // Social proof counter
  const [totalCompletions, setTotalCompletions] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch stats for social proof on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/career-readiness-check/stats`);
        if (response.ok) {
          const data = await response.json();
          setTotalCompletions(data.total_completions || 0);
        }
      } catch (error) {
        // Silent fail - will show fallback text
      }
    };
    fetchStats();
  }, [API_URL]);

  // Load V3 questions, then resume an in-progress attempt (same shuffled order) if there is one
  useEffect(() => {
    fetch(`${API_URL}/api/career-readiness-check/v3/questions`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        setBank(data);
        const saved = loadAttempt(data.questions);
        if (saved) {
          setAttempt(saved);
          setAnswers(saved.answers || {});
          setCurrentQuestion(Math.min(saved.current || 0, data.questions.length - 1));
          setStep(saved.step);
        }
      })
      .catch(() => setBankError(true));
  }, [API_URL]);

  useEffect(() => {
    if (attempt && ['assessment', 'lead-capture'].includes(step)) saveAttempt({ ...attempt, answers, current: currentQuestion, step });
  }, [attempt, answers, currentQuestion, step]);

  const questions = useMemo(() => {
    if (!bank || !attempt) return [];
    return bank.questions.map((q) => {
      const byId = Object.fromEntries(q.options.map((o) => [o.id, o]));
      return { ...q, options: attempt.order[q.id].map((id) => byId[id]) };
    });
  }, [bank, attempt]);

  // Talent Network hand-off: skip the details step for registered members
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('t');
    const linkCode = params.get('m'); // short personal link shared by the team on WhatsApp
    if (fromUrl) sessionStorage.setItem('elevate_readiness_handoff', fromUrl);
    if (fromUrl || linkCode) {
      params.delete('t');
      params.delete('m');
      window.history.replaceState(null, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
    }
    const resolve = linkCode
      ? fetch(`${API_URL}/api/career-readiness-check/link/${encodeURIComponent(linkCode)}`).then((r) => (r.ok ? r.json() : {})).then((d) => {
        if (d.token) sessionStorage.setItem('elevate_readiness_handoff', d.token);
      }).catch(() => {})
      : Promise.resolve();
    resolve.then(() => {
      const token = sessionStorage.getItem('elevate_readiness_handoff');
      if (!token) return;
      fetch(`${API_URL}/api/career-readiness-check/handoff?token=${encodeURIComponent(token)}`)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => setHandoff({ token, firstName: d.first_name }))
        .catch(() => sessionStorage.removeItem('elevate_readiness_handoff'));
    });
  }, [API_URL]);

  // Let the team see that a Talent Network member has started (and how far they got)
  useEffect(() => {
    if (!handoff || step !== 'assessment') return;
    fetch(`${API_URL}/api/career-readiness-check/progress`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: handoff.token, question: currentQuestion + 1 }),
    }).catch(() => {});
  }, [API_URL, handoff, step, currentQuestion]);

  // Get social proof text based on completions - compelling without fabrication
  const getSocialProofText = () => {
    if (totalCompletions >= 1000) {
      return "1,000+ students already know their gaps";
    } else if (totalCompletions >= 500) {
      return "500+ students already know their gaps";
    } else if (totalCompletions >= 250) {
      return "250+ students already know their gaps";
    } else if (totalCompletions >= 100) {
      return "100+ students already know their gaps";
    }
    // Compelling FOMO copy when numbers are low
    return "Free today · Results in 3 minutes";
  };

  // Start the assessment - GA EVENT PRESERVED
  const startAssessment = () => {
    if (!bank) return;
    const fresh = newAttempt(bank.questions);
    setAttempt(fresh);
    setAnswers({});
    setStep('assessment');
    setCurrentQuestion(0);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'career_check_started');
    }
  };

  // Handle answer selection
  const selectAnswer = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  // Go to next question - GA EVENT PRESERVED
  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      // Track midpoint - GA EVENT PRESERVED
      if (currentQuestion === 5 && typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'career_check_question_6_reached');
      }
    } else {
      // Track completion - GA EVENT PRESERVED
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'career_check_completed');
      }
      if (handoff) submitLead();
      else setStep('lead-capture');
    }
  };

  // Go to previous question
  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // Handle lead form change
  const handleLeadChange = (e) => {
    const { name, value } = e.target;
    setLeadData(prev => ({ ...prev, [name]: value }));
  };

  // Submit lead form - GA EVENT PRESERVED
  const submitLead = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/career-readiness-check/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(handoff ? { handoff_token: handoff.token } : {
            lead: { firstName: leadData.firstName, email: leadData.email, whatsapp: leadData.whatsapp },
            email_updates_optin: consent.email,
            whatsapp_optin: consent.whatsapp && !!leadData.whatsapp,
          }),
          answers,
          assessment_version: bank.version,
          utmParams: new URLSearchParams(window.location.search).toString(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setOutcome((o) => ({ ...o, error: data.detail || 'Something went wrong. Please try again.' }));
        if (handoff) { sessionStorage.removeItem('elevate_readiness_handoff'); setHandoff(null); setStep('lead-capture'); }
        setSubmitting(false);
        return;
      }
      if (typeof window !== 'undefined' && window.gtag) window.gtag('event', 'career_check_lead_submitted');
      if (handoff) sessionStorage.removeItem('elevate_readiness_handoff');
      setLeadData((l) => ({ ...l, firstName: data.first_name || l.firstName }));
      setOutcome({ isMember: !!data.is_member, emailedTo: data.results_emailed_to || '', error: '', prefill: data.prefill || null });
      clearAttempt();
      setResults(data.results);
      setSubmitting(false);
      setStep('results');
    } catch (error) {
      setOutcome((o) => ({ ...o, error: "We couldn't load your result. Please check your connection and try again." }));
      setSubmitting(false);
      setStep('lead-capture');
    }
  };

  // Get band color for results
  const getBandColor = (band) => {
    switch (band) {
      case 'Strong': return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30';
      case 'Developing Well': return 'text-blue-500 bg-blue-500/10 border-blue-500/30';
      case 'Needs Attention': return 'text-amber-500 bg-amber-500/10 border-amber-500/30';
      case 'Priority Gap': return 'text-orange-500 bg-orange-500/10 border-orange-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  // Get score bar color
  const getScoreBarColor = (score) => {
    const percentage = (score / 12) * 100;
    if (percentage >= 85) return 'bg-emerald-500';
    if (percentage >= 70) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-orange-500';
  };

  // Dimension display names
  const dimensionNames = {
    career_direction: 'Career Direction',
    profile_strength: 'Profile Strength',
    interview_readiness: 'Interview Readiness',
    job_search: 'Job Search',
  };

  // Progress message
  const getProgressMessage = (questionNum) => {
    if (questionNum === 3) return "You're 25% there";
    if (questionNum === 6) return "Halfway there";
    if (questionNum === 10) return "Almost done";
    return null;
  };

  // ============================================
  // LANDING PAGE - PREMIUM TWO-COLUMN DESIGN
  // ============================================
  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Minimal Header */}
        <header className="py-4 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <Link to="/" className="inline-block">
              <div className="bg-white border border-slate-200 px-3 py-1.5 rounded-xl inline-block">
                <img 
                  src="/logo-elevate-careers.webp" 
                  alt="Elevate Careers" 
                  className="h-8 w-auto"
                />
              </div>
            </Link>
            <Link to="/my-journey" data-testid="header-my-journey" className="text-sm font-semibold text-teal-700 border border-teal-200 hover:bg-teal-50 px-4 py-2 rounded-full whitespace-nowrap">My Career Journey</Link>
          </div>
        </header>

        {/* Hero Section - Two Column on Desktop */}
        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              
              {/* Left Column - Content */}
              <div className="text-center lg:text-left">
                {/* Eyebrow */}
                <p className="text-xs font-semibold tracking-widest text-teal-700 mb-4">
                  FREE · 3 MINUTES · 12 QUESTIONS
                </p>

                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
                  You're putting in the effort.
                  <span className="block text-teal-700 mt-1">But what's actually holding you back?</span>
                </h1>

                {/* Supporting line */}
                <p className="text-lg text-slate-600 mb-6 max-w-md mx-auto lg:mx-0">
                  Find out whether the gap is in your career direction, profile, interviews or job search.
                </p>

                {/* Primary CTA */}
                <div className="flex flex-col items-center lg:items-start">
                  <Button 
                    onClick={startAssessment}
                    disabled={!bank}
                    className="bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] px-8 py-6 text-lg rounded-full shadow-lg shadow-teal-600/20 w-full sm:w-auto"
                    data-testid="start-assessment-btn"
                  >
                    Find My Career Gap
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  {bankError && <p className="text-sm text-red-600 mt-3" data-testid="questions-load-error">We couldn't load the questions. Please refresh the page.</p>}
                  
                  {/* Social Proof Line */}
                  <p className="text-sm text-slate-500 mt-4">
                    {getSocialProofText()}
                  </p>
                  {handoff && (
                    <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mt-3" data-testid="handoff-member-note">
                      Welcome, {handoff.firstName}. Your Talent Network details are saved, so you'll see your results right after the last question.
                    </p>
                  )}
                </div>
              </div>

              {/* Right Column - Sample Result Card */}
              <div className="mt-8 lg:mt-0">
                <div className="relative">
                  {/* Card Container */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden max-w-sm mx-auto lg:max-w-none">
                    {/* Card Header */}
                    <div className="bg-[#0b1f4d] px-6 py-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sample Result</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                          <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                          <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                        </div>
                      </div>
                      <h3 className="text-white font-semibold mt-1">Career Readiness Score</h3>
                    </div>

                    {/* Score Bars */}
                    <div className="p-6 space-y-4">
                      {[
                        { label: 'Career Direction', score: 78, color: 'bg-emerald-500' },
                        { label: 'Profile Strength', score: 54, color: 'bg-amber-500' },
                        { label: 'Interview Readiness', score: 46, color: 'bg-orange-500', isLowest: true },
                        { label: 'Job Search', score: 67, color: 'bg-blue-500' },
                      ].map((item) => (
                        <div key={item.label}>
                          <div className="flex justify-between items-center mb-1.5">
                            <span className={`text-sm ${item.isLowest ? 'text-slate-900 font-semibold' : 'text-slate-600'}`}>
                              {item.label}
                            </span>
                            <span className={`text-sm font-bold ${item.isLowest ? 'text-orange-600' : 'text-slate-900'}`}>
                              {item.score}
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${item.color} rounded-full`}
                              style={{ width: `${item.score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Highlight Section */}
                    <div className="px-6 pb-6">
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">Your biggest gap</p>
                        <p className="text-lg font-bold text-slate-900">Interview Readiness</p>
                        <p className="text-sm text-slate-600 mt-1">Know what to work on first.</p>
                      </div>
                    </div>
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute -z-10 -top-4 -right-4 w-24 h-24 bg-teal-50 rounded-full blur-2xl opacity-60"></div>
                  <div className="absolute -z-10 -bottom-4 -left-4 w-32 h-32 bg-amber-100 rounded-full blur-2xl opacity-60"></div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Four Readiness Areas - Compact Chips */}
        <section className="py-8 px-4 bg-white border-t border-slate-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: Compass, label: 'Career Direction', color: 'bg-teal-50 text-teal-700 border-teal-200' },
                { icon: FileText, label: 'Profile Strength', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                { icon: MessageSquare, label: 'Interview Readiness', color: 'bg-amber-50 text-amber-700 border-amber-200' },
                { icon: Search, label: 'Job Search', color: 'bg-blue-50 text-blue-700 border-blue-200' },
              ].map((area) => (
                <div 
                  key={area.label}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border ${area.color}`}
                >
                  <area.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{area.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Relatability Section - Ultra Compact */}
        <section className="py-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Sound familiar?</h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { text: "Applying.", sub: "No callbacks." },
                { text: "Courses.", sub: "Still confused." },
                { text: "Interviews.", sub: "No conversions." },
                { text: "Too many roles.", sub: "No direction." },
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-white border border-slate-200 rounded-xl px-4 py-4 text-center shadow-sm"
                >
                  <p className="text-sm font-semibold text-slate-900">{item.text}</p>
                  <p className="text-sm text-slate-500">{item.sub}</p>
                </div>
              ))}
            </div>

            <Button 
              onClick={startAssessment}
              disabled={!bank}
              data-testid="start-assessment-btn-secondary"
              className="bg-[#0b1f4d] hover:bg-[#13296b] text-white rounded-full px-6 py-3"
            >
              Find out what to fix first
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </section>

        {/* Footer Disclaimer */}
        <footer className="py-6 px-4 bg-white border-t border-slate-100">
          <p className="text-center text-xs text-slate-400 max-w-md mx-auto">
            This is a self-assessment designed to help you identify areas worth reviewing. It does not guarantee interview or employment outcomes.
          </p>
        </footer>
      </div>
    );
  }

  // ============================================
  // ASSESSMENT FLOW - REDESIGNED
  // ============================================
  if (step === 'assessment' && questions.length) {
    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const selectedAnswer = answers[question.id];
    const progressMsg = getProgressMessage(currentQuestion + 1);

    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Progress Header */}
        <div className="bg-white border-b border-slate-100 py-4 px-4 sticky top-0 z-10">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-900">
                {currentQuestion + 1} of {questions.length}
              </span>
              <button type="button" onClick={() => window.confirm('Start over? Your answers so far will be cleared.') && startAssessment()} data-testid="restart-assessment-btn"
                className="text-xs text-slate-400 hover:text-teal-700 underline-offset-2 hover:underline">Start over</button>
              {progressMsg && (
                <span className="text-sm text-teal-700 font-medium">{progressMsg}</span>
              )}
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#0b1f4d] transition-all duration-300 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 flex items-center justify-center py-8 px-4">
          <div className="max-w-xl w-full">
            {question.dimension === 'job_search' && bank.job_search_intro && (
              <p className="text-sm text-teal-800 bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 mb-5" data-testid="job-search-intro">{bank.job_search_intro}</p>
            )}
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-8 leading-relaxed" data-testid="question-text">
              {question.question}
            </h2>

            <div className="space-y-3">
              {question.options.map((option, index) => (
                <button
                  key={option.id}
                  onClick={() => selectAnswer(question.id, option.id)}
                  className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all ${
                    selectedAnswer === option.id
                      ? 'bg-teal-50 border-teal-500 text-slate-900'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`option-${index}`}
                  data-option-id={option.id}
                  aria-pressed={selectedAnswer === option.id}
                >
                  <span className="text-base leading-relaxed">{option.text}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="bg-white border-t border-slate-100 py-4 px-4 sticky bottom-0">
          <div className="max-w-xl mx-auto flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
              className="text-slate-500 hover:text-slate-900"
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={nextQuestion}
              disabled={!selectedAnswer || submitting}
              className="bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] px-8 rounded-full"
              data-testid="next-btn"
            >
              {submitting ? 'Loading your result…' : currentQuestion === questions.length - 1 ? 'See My Result' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // LEAD CAPTURE - MINIMAL FRICTION
  // ============================================
  if (step === 'lead-capture') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center py-8 px-4">
        <div className="max-w-sm w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-teal-700" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Your Career Readiness Result is ready.
            </h1>
            <p className="text-slate-500">
              Tell us where to send it. We'll show your result right away and email you a copy.
            </p>
          </div>

          <form onSubmit={submitLead} className="space-y-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1.5">
                First Name
              </label>
              <Input
                type="text"
                name="firstName"
                value={leadData.firstName}
                onChange={handleLeadChange}
                required
                autoComplete="given-name"
                className="bg-white border-slate-300 text-slate-900 rounded-xl h-12"
                placeholder="Your first name"
                data-testid="lead-firstname"
              />
            </div>

            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1.5">
                Email Address
              </label>
              <Input
                type="email"
                name="email"
                value={leadData.email}
                onChange={handleLeadChange}
                required
                autoComplete="email"
                inputMode="email"
                className="bg-white border-slate-300 text-slate-900 rounded-xl h-12"
                placeholder="you@example.com"
                data-testid="lead-email"
              />
            </div>

            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1.5">
                WhatsApp Number <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="flex gap-2">
                <div className="flex items-center bg-slate-100 border border-slate-300 rounded-xl px-3 h-12 text-slate-600 text-sm font-medium">
                  +91
                </div>
                <Input
                  type="tel"
                  name="whatsapp"
                  value={leadData.whatsapp}
                  onChange={handleLeadChange}
                  autoComplete="tel-national"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  className="bg-white border-slate-300 text-slate-900 rounded-xl h-12 flex-1"
                  placeholder="98765 43210"
                  data-testid="lead-whatsapp"
                />
              </div>
            </div>

            <div className="space-y-2.5 pt-1">
              <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={consent.email} onChange={(e) => setConsent((c) => ({ ...c, email: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-teal-600" data-testid="lead-email-optin" />
                Email me career tips, opportunities and offers from Elevate Careers
              </label>
              <label className={`flex items-start gap-2.5 text-sm cursor-pointer ${leadData.whatsapp ? 'text-slate-600' : 'text-slate-400'}`}>
                <input type="checkbox" disabled={!leadData.whatsapp} checked={consent.whatsapp && !!leadData.whatsapp} onChange={(e) => setConsent((c) => ({ ...c, whatsapp: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-teal-600" data-testid="lead-whatsapp-optin" />
                Message me on WhatsApp about my results and next steps
              </label>
            </div>

            {outcome.error && <p className="text-sm text-red-600" data-testid="lead-error">{outcome.error}</p>}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] py-6 rounded-full mt-6 text-lg font-semibold"
              data-testid="submit-lead-btn"
            >
              {submitting ? 'Loading...' : 'Show My Results'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-4">
            We only send updates if you tick a box above. Your results email is sent either way.
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // RESULTS PAGE - WITH PROGRESSIVE PROFILING
  // ============================================
  if (step === 'results' && results) {
    const content = priorityAreaContent[results.priorityArea];
    
    // Convert raw score (3-12) to percentage (0-100)
    const scoreToPercentage = (score) => Math.round(((score - 3) / 9) * 100);

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="py-4 px-4 border-b border-slate-100">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <Link to="/" className="inline-block">
              <div className="bg-white border border-slate-200 px-3 py-1.5 rounded-xl inline-block">
                <img 
                  src="/logo-elevate-careers.webp" 
                  alt="Elevate Careers" 
                  className="h-8 w-auto"
                />
              </div>
            </Link>
            <Link to="/my-journey" data-testid="header-my-journey" className="text-sm font-semibold text-teal-700 border border-teal-200 hover:bg-teal-50 px-4 py-2 rounded-full whitespace-nowrap">My Career Journey</Link>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Greeting */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Hi {leadData.firstName}, here's your Career Readiness snapshot
            </h1>
          </div>

          {/* Scores Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Your Readiness Scores</h2>
            
            <div className="space-y-5">
              {Object.entries(results.scores).map(([dimension, score]) => {
                const percentage = scoreToPercentage(score);
                const band = results.bands[dimension];
                return (
                  <div key={dimension}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-slate-700">{dimensionNames[dimension]}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-slate-900">{percentage}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getBandColor(band)}`}>
                          {band}
                        </span>
                      </div>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getScoreBarColor(score)} rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <ReadinessInsights results={results} />

          {/* Priority Area */}
          {!results.allStrong && (
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3">
                {content.heading}
              </h2>
              <p className="text-slate-700 mb-4 leading-relaxed">
                {results.hasTie ? content.tieExplanation : content.copy}
              </p>
              
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Why this matters:</h3>
              <p data-testid="priority-why-matters" className="text-slate-600 text-sm">{content.whyMatters}</p>
            </div>
          )}

          {results.allStrong && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3">
                Looking Good Across the Board
              </h2>
              <p className="text-slate-700 leading-relaxed">
                Your responses suggest that you have a reasonable foundation across all four areas. 
                To maintain momentum, continue refining your preparation and consider working with 
                a mentor or coach for targeted feedback.
              </p>
            </div>
          )}

          <ReadinessNextStep area={results.priorityArea} />

          {/* Next steps */}
          <div className="bg-[#0b1f4d] rounded-2xl p-6 text-center" data-testid="results-next-steps">
            <h3 className="text-xl font-bold text-white mb-2">Want an expert to walk you through your result?</h3>
            <p className="text-slate-300 mb-6 text-sm">Book a 1:1 Career Clarity Consultation for personalised guidance on your priority area.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/apply?plan=clarity-session" data-testid="results-book-consultation-btn"
                onClick={() => outcome.prefill && sessionStorage.setItem('elevate_booking_prefill', JSON.stringify(outcome.prefill))}>
                <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6">
                  Book Career Clarity Consultation · ₹499
                </Button>
              </Link>
              {!outcome.isMember && !handoff && (
                <Link to="/network" data-testid="results-join-network-btn">
                  <Button variant="outline" className="w-full bg-transparent border-slate-500 text-white hover:bg-[#13296b] hover:text-white rounded-full px-6">
                    Join the Talent Network · Free
                  </Button>
                </Link>
              )}
            </div>
            {outcome.emailedTo && <p className="text-xs text-slate-400 mt-5" data-testid="results-emailed-note">We've also emailed your results to {outcome.emailedTo}.</p>}
          </div>

          {/* Disclaimer */}
          <p className="text-center text-xs text-slate-500 mt-8" data-testid="results-disclaimer">
            These results reflect your self-reported career preparation. They are not a verified measure of job readiness
            and do not guarantee interviews or employment.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default CareerReadinessCheck;
